import type {
  LocalPermissions,
  Roles,
  RolesListParams,
  RolesToPermissions,
  RolesToPermissionsListData,
  RoleToPermissionsPayload,
} from '@/libs/interfaces'

import { MOCK_ORG_ID, mockPermissions, mockRoles } from '../fixtures/seed'
import { delay, genID, matchesSearch } from '../helpers'
import { createEntityStore } from '../store'

const roleStore = createEntityStore<Roles, 'roleID'>(mockRoles, 'roleID')

export const rolesApiMock = {
  create: async (body: Partial<Roles>): Promise<Partial<Roles>> => {
    const role: Roles = {
      roleID: genID('role'),
      roleName: '',
      description: '',
      orgID: MOCK_ORG_ID,
      ...body,
    }
    roleStore.insert(role)
    return delay(role)
  },
  delete: async (payload: Partial<Roles>) => {
    if (payload.roleID) roleStore.remove(payload.roleID)
    return delay(undefined)
  },
  getByID: async (params: Partial<Roles>): Promise<Partial<Roles>> =>
    delay(roleStore.getById(params.roleID ?? '') ?? {}),
  list: async (params: RolesListParams): Promise<Roles[]> =>
    delay(roleStore.getAll().filter((role) => matchesSearch(role, params.search, ['roleName']))),
  update: async (payload: Partial<Roles>) => {
    const updated = payload.roleID ? roleStore.update(payload.roleID, payload) : undefined
    return delay(updated ?? payload)
  },
  // Mock mode always grants full access — every role resolves to "manage all".
  createRolesToPermissions: async (
    _params: Partial<RolesToPermissions>,
    _payload: RoleToPermissionsPayload,
  ): Promise<Partial<RolesToPermissionsListData>> =>
    delay({ roleID: _params.roleID, allPermissionsWithStatus: mockPermissions }),
  getRolesToPermissions: async (_params: Partial<Roles>): Promise<LocalPermissions[]> =>
    delay(mockPermissions),
  deleteRolesToPermissions: async (params: Partial<Roles>): Promise<Partial<RolesToPermissions>> =>
    delay({ roleID: params.roleID }),
}
